import { FC, useState } from 'react';
import {
  useTranslation,
  AlertMessage,
  Theme,
  Drawer,
  Button,
  ButtonStyles,
  ButtonWidth,
  ButtonSize,
} from '@nextgen-web-framework/all';
import type { AlertMessageTheme } from '@nextgen-web-framework/all';
import {
  ContactType,
  DynamicObjectInterface,
  ExtendedContactType,
  OwnerType,
} from '@commerce-profile-webcomponents/sdk';
import { parseString } from '../../../utils/helper';
import { getAddressByContactId } from '../../../utils/contactUtil';
import SkeletonLoader from '../../Common/skeleton-loader/SkeletonLoader';
import ButtonWithLoading from '../../Common/ButtonWithLoading/ButtonWithLoading';
import AddressInput from '../../AddressInput/AddressInput';
import MessageModal from '../../Common/MessageModal/MessageModal';
import { useGlobalErrorHandler } from '../../../utils/globalErrorHook';
import { MessageModalProps } from '../../Common/MessageModal/types/propTypes';
import { BadAddressAlertPropsWithValidation } from './types/propTypes';
import styles from './BadAddressAlert.module.scss';

const BadAddressAlert: FC<BadAddressAlertPropsWithValidation> = ({
  message,
  showChangeCTA,
  address,
  onConfirmAddress,
  theme = Theme.ERROR,
  customCTA,
  disableDeliveryInstructions = false,
}) => {
  const t = useTranslation();
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputFormCanSubmit, setInputFormCanSubmit] = useState(false);
  const [updatedAddress, setUpdatedAddress] = useState<ExtendedContactType | undefined>(undefined);
  const [modalProps, setModalProps] = useState<MessageModalProps>({
    headline: '',
  });
  const apiErrorHandler = useGlobalErrorHandler();
  const defaultMessage = 'profile.addressCard.addressUpdateRequired';

  const fetchAndShowAddress = async () => {
    if (!address?.contactId) {
      setModalProps({
        onClose: closeModalHandler,
        headline: `profile.common.errorHeading`,
        message: `profile.addressBook.unableToLoad`,
      });
    } else {
      try {
        setIsLoading(true);
        setShowAddressForm(true);
        const addressRes = await getAddressByContactId(address.contactId, apiErrorHandler);
        if (addressRes) {
          setUpdatedAddress(address);
          setIsLoading(false);
        }
      } catch (error) {
        setModalProps({
          onClose: closeModalHandler,
          headline: `profile.common.errorHeading`,
          message: `profile.addressBook.unableToLoad`,
        });
        setIsLoading(false);
      }
    }
  };

  const onDrawerClose = () => {
    setShowAddressForm(false);
  };

  const handleFormCanSubmit = (canSubmit: boolean) => {
    setInputFormCanSubmit(canSubmit);
  };

  const handleSubmit = (value?: ExtendedContactType) => {
    setIsLoading(true);

    onConfirmAddress?.(value as DynamicObjectInterface);

    setShowAddressForm(false);
    setIsLoading(false);
  };

  const closeModalHandler = () => {
    setModalProps({
      headline: '',
    });
  };

  const textLink =
    customCTA ||
    (showChangeCTA && {
      text: t('profile.common.update'),
      onClick: fetchAndShowAddress,
    }) ||
    undefined;

  return (
    <>
      <div className={styles['bad-address-alert-container']}>
        <AlertMessage
          className={styles['bad-address-alert']}
          content={{
            heading: message ?? t(defaultMessage),
          }}
          theme={theme ?? (Theme.ERROR as AlertMessageTheme)}
          {...(textLink && {
            textLink: {
              ...textLink,
              onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                e.preventDefault(); // to prevent page reload
                textLink?.onClick?.(e);
              },
            },
          })}
        />
      </div>
      {showAddressForm && (
        <Drawer
          header={<span>{t('profile.addressBook.editAddressLabel')}</span>}
          useReactPortal={true}
          portalContainerId={'react-portal-drawer'}
          closeIcon
          className={styles['address-book-drawer']}
          onClose={onDrawerClose}
          bottomGap={0}
          footer={
            <div className={styles['confirm-button-group']}>
              <Button
                data-testId="back-button-id"
                title={t('profile.addressBook.backButton')}
                buttonStyle={ButtonStyles.OUTLINE}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                size={ButtonSize.MEDIUM}
                onClick={onDrawerClose}
              />
              <ButtonWithLoading
                data-testId="confrim-button-id"
                form="shipping-address-form-id"
                title={t('profile.addressBook.confirmButton')}
                buttonStyle={ButtonStyles.SOLID}
                size={ButtonSize.MEDIUM}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                disabled={isLoading || !inputFormCanSubmit}
                isLoading={isLoading}
              />
            </div>
          }
        >
          {isLoading ? (
            <SkeletonLoader noOfSkeletons={4} />
          ) : (
            <AddressInput
              address={updatedAddress}
              hideConfirmButton={true}
              isEdit={true}
              callback={handleSubmit}
              accountNumber={parseString(updatedAddress?.accountNumber)}
              ownerId={parseString(updatedAddress?.ownerId)}
              contactType={updatedAddress?.contactType as ContactType}
              ownerType={OwnerType.Profile}
              showDefaultAddressCheckbox={false}
              // Use mobile view in drawer, because it's showing in single column
              useMobileView={true}
              updateDisableButton={handleFormCanSubmit}
              disableDeliveryInstructions={disableDeliveryInstructions}
            />
          )}
        </Drawer>
      )}
      <MessageModal {...modalProps} />
    </>
  );
};

export default BadAddressAlert;
